import { Controller } from "@hotwired/stimulus"

const MAX_LINES = 5
const DEBOUNCE_MS = 350

export default class extends Controller {
  static targets = [
    "card", "addButton", "total", "template",
    "sharedStartDate", "sharedEndDate",
    "rentalSelect", "overrideToggle", "overrideSection"
  ]
  static values = { previewUrl: String, availableUnitsUrl: String }

  #debounceTimer = null
  // Cached available-units payload per date-range key, avoids duplicate fetches within
  // the page lifetime. Cache is intentionally not invalidated on submit — the form is
  // short-lived (admin tab) and the staleness window is acceptable.
  #unitsCache = {}

  connect() {
    this.#updateAddButton()
    this.#updateTotal()
    this.#refreshAllDropdowns()
    // Run initial preview on any pre-filled cards (e.g. 422 re-render)
    this.cardTargets.forEach(card => this.#previewCard(card))
  }

  // ── Shared date range changed ──────────────────────────────────────────────

  sharedDatesChanged() {
    clearTimeout(this.#debounceTimer)
    this.#debounceTimer = setTimeout(() => this.#refreshAllDropdowns(), DEBOUNCE_MS)
  }

  // ── Per-line override dates changed ───────────────────────────────────────

  overrideDatesChanged(event) {
    const card = event.target.closest("[data-group-booking-cart-target='card']")
    if (!card) return
    clearTimeout(this.#debounceTimer)
    this.#debounceTimer = setTimeout(() => this.#refreshCardDropdown(card), DEBOUNCE_MS)
  }

  // ── Toggle the per-line date-override section ──────────────────────────────

  toggleDateOverride(event) {
    const card = event.currentTarget.closest("[data-group-booking-cart-target='card']")
    if (!card) return

    const btn     = event.currentTarget
    const section = card.querySelector("[data-group-booking-cart-target='overrideSection']")
    const chevron = btn.querySelector("[data-override-chevron]")
    const expanded = btn.getAttribute("aria-expanded") === "true"

    btn.setAttribute("aria-expanded", expanded ? "false" : "true")
    section?.classList.toggle("hidden", expanded)
    chevron?.classList.toggle("rotate-90", !expanded)

    if (expanded) {
      // Closing the override: clear override fields so the shared range is used
      const overrideStart = card.querySelector("[data-line-field='override_start_date']")
      const overrideEnd   = card.querySelector("[data-line-field='override_end_date']")
      if (overrideStart) overrideStart.value = ""
      if (overrideEnd)   overrideEnd.value   = ""
      // Repopulate the dropdown with the shared-range units
      this.#refreshCardDropdown(card)
    } else {
      // Opening the override: pre-fill the shared dates as defaults
      const sharedStart = this.#sharedStartDate()
      const sharedEnd   = this.#sharedEndDate()
      const overrideStart = card.querySelector("[data-line-field='override_start_date']")
      const overrideEnd   = card.querySelector("[data-line-field='override_end_date']")
      if (overrideStart && !overrideStart.value) overrideStart.value = sharedStart
      if (overrideEnd   && !overrideEnd.value)   overrideEnd.value   = sharedEnd
    }
  }

  // ── Add / remove cards ────────────────────────────────────────────────────

  addLine() {
    if (this.cardTargets.length >= MAX_LINES) return

    const index   = this.cardTargets.length
    const newCard = this.#cloneTemplate(index)
    this.addButtonTarget.closest("[data-group-booking-cart-target='addWrapper']")
      ?.before(newCard) ?? this.addButtonTarget.before(newCard)

    // Populate the dropdown for the new card immediately
    const addedCard = this.cardTargets[this.cardTargets.length - 1]
    if (addedCard) this.#refreshCardDropdown(addedCard)

    this.#updateAddButton()
    this.#updateTotal()
  }

  removeLine(event) {
    if (this.cardTargets.length <= 1) return

    event.currentTarget.closest("[data-group-booking-cart-target='card']").remove()
    this.#reindex()
    this.#updateAddButton()
    this.#updateTotal()
  }

  // ── Live price preview ────────────────────────────────────────────────────

  previewLine(event) {
    const card = event.target.closest("[data-group-booking-cart-target='card']")
    if (card) this.#previewCard(card)
  }

  // ── private ───────────────────────────────────────────────────────────────

  #sharedStartDate() {
    return this.hasSharedStartDateTarget ? this.sharedStartDateTarget.value.trim() : ""
  }

  #sharedEndDate() {
    return this.hasSharedEndDateTarget ? this.sharedEndDateTarget.value.trim() : ""
  }

  // The effective date range for a card: override dates if set, else shared range.
  #effectiveDates(card) {
    const overrideStart = this.#fieldValue(card, "override_start_date")
    const overrideEnd   = this.#fieldValue(card, "override_end_date")
    const start = overrideStart || this.#sharedStartDate()
    const end   = overrideEnd   || this.#sharedEndDate()
    return { start, end }
  }

  #refreshAllDropdowns() {
    this.cardTargets.forEach(card => this.#refreshCardDropdown(card))
  }

  async #refreshCardDropdown(card) {
    const { start, end } = this.#effectiveDates(card)
    const select = card.querySelector("[data-group-booking-cart-target='rentalSelect']")
    if (!select) return

    if (!start || !end) {
      this.#setSelectEmpty(select, "Choose dates to see available units")
      this.#previewCard(card)
      return
    }

    const cacheKey = `${start}|${end}`
    const token = (card.dataset.fetchToken = String(Date.now()))

    const units = this.#unitsCache[cacheKey] ?? await this.#fetchAvailableUnits(start, end)

    // A newer request for this card superseded us — discard this stale result.
    if (card.dataset.fetchToken !== token) return

    if (units === null) {
      this.#setSelectEmpty(select, "Could not load available units")
      return
    }

    if (units !== null) this.#unitsCache[cacheKey] = units
    this.#populateSelect(select, units)
    this.#previewCard(card)
  }

  async #fetchAvailableUnits(startDate, endDate) {
    if (!this.hasAvailableUnitsUrlValue) return null

    const url = new URL(this.availableUnitsUrlValue, window.location.origin)
    url.searchParams.set("start_date", startDate)
    url.searchParams.set("end_date",   endDate)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(url.toString(), {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken || ""
        },
        credentials: "same-origin"
      })
      if (!response.ok) return null
      return await response.json()
    } catch {
      return null
    }
  }

  #populateSelect(select, units) {
    const previousId = select.value

    select.innerHTML = ""
    const placeholder = document.createElement("option")
    placeholder.value = ""
    placeholder.textContent = units.length === 0
      ? "No units available for these dates"
      : "— Select a site —"
    select.appendChild(placeholder)

    units.forEach(unit => {
      const opt = document.createElement("option")
      opt.value = unit.id
      opt.textContent = `${unit.name} ($${unit.price.toFixed(2)}/night, ${unit.capacity} guest${unit.capacity !== 1 ? "s" : ""} max)`
      // Restore previous selection when the unit is still in the list
      if (String(unit.id) === String(previousId)) opt.selected = true
      select.appendChild(opt)
    })

    select.disabled = units.length === 0
  }

  #setSelectEmpty(select, message) {
    select.innerHTML = ""
    const opt = document.createElement("option")
    opt.value = ""
    opt.textContent = message
    select.appendChild(opt)
    select.disabled = true
  }

  #cloneTemplate(index) {
    const tpl  = this.templateTarget.content.cloneNode(true)
    const card = tpl.querySelector("[data-group-booking-cart-target='card']")

    const heading = card.querySelector("[data-card-heading]")
    if (heading) heading.textContent = `Site ${index + 1}`

    card.querySelectorAll("[data-line-field]").forEach(el => {
      const field = el.dataset.lineField
      const name  = `booking[lines_attributes][${index}][${field}]`
      const id    = `booking_lines_${index}_${field}`
      el.setAttribute("name", name)
      el.setAttribute("id", id)
      // Reset values; keep guest_count at 1
      if (el.tagName === "SELECT") {
        // leave; #refreshCardDropdown will populate
      } else if (el.type === "checkbox") {
        el.checked = false
      } else {
        el.value = field === "guest_count" ? "1" : ""
      }
    })
    card.querySelectorAll("[data-line-label]").forEach(el => {
      const field = el.dataset.lineLabel
      el.setAttribute("for", `booking_lines_${index}_${field}`)
    })

    return tpl
  }

  #reindex() {
    this.cardTargets.forEach((card, index) => {
      const heading = card.querySelector("[data-card-heading]")
      if (heading) heading.textContent = `Site ${index + 1}`

      card.querySelectorAll("[data-line-field]").forEach(el => {
        const field = el.dataset.lineField
        el.setAttribute("name", `booking[lines_attributes][${index}][${field}]`)
        el.setAttribute("id", `booking_lines_${index}_${field}`)
      })
      card.querySelectorAll("[data-line-label]").forEach(el => {
        const field = el.dataset.lineLabel
        el.setAttribute("for", `booking_lines_${index}_${field}`)
      })
    })
  }

  #updateAddButton() {
    if (!this.hasAddButtonTarget) return
    this.addButtonTarget.disabled = this.cardTargets.length >= MAX_LINES
  }

  #updateTotal() {
    if (!this.hasTotalTarget) return
    let sum = 0
    this.cardTargets.forEach(card => {
      const stored = parseFloat(card.dataset.lineSubtotal || "0")
      if (!isNaN(stored)) sum += stored
    })
    this.totalTarget.textContent = this.#formatCurrency(sum)
  }

  async #previewCard(card) {
    if (!this.hasPreviewUrlValue) return

    const rentalId = this.#fieldValue(card, "rental_id")
    const guests   = this.#fieldValue(card, "guest_count")
    const { start: startDate, end: endDate } = this.#effectiveDates(card)

    const errorEl    = card.querySelector("[data-line-error]")
    const subtotalEl = card.querySelector("[data-line-subtotal]")

    if (!rentalId || !startDate || !endDate || !guests) {
      if (errorEl)    { errorEl.textContent = ""; errorEl.classList.add("hidden") }
      if (subtotalEl) subtotalEl.textContent = ""
      card.dataset.lineSubtotal = "0"
      this.#updateTotal()
      return
    }

    const url = new URL(this.previewUrlValue, window.location.origin)
    url.searchParams.set("rental_id",   rentalId)
    url.searchParams.set("start_date",  startDate)
    url.searchParams.set("end_date",    endDate)
    url.searchParams.set("guest_count", guests)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(url.toString(), {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken || ""
        },
        credentials: "same-origin"
      })

      if (!response.ok) {
        this.#setLineState(card, errorEl, subtotalEl, null, "Could not load pricing")
        return
      }

      const data = await response.json()

      if (data.available) {
        this.#setLineState(card, errorEl, subtotalEl, parseFloat(data.total_price || "0"), null)
      } else {
        this.#setLineState(card, errorEl, subtotalEl, null, data.error || "Not available")
      }
    } catch {
      this.#setLineState(card, errorEl, subtotalEl, null, "Could not load pricing")
    }
  }

  #setLineState(card, errorEl, subtotalEl, price, error) {
    if (errorEl) {
      errorEl.textContent = error || ""
      errorEl.classList.toggle("hidden", !error)
    }
    if (subtotalEl) {
      subtotalEl.textContent = price != null ? this.#formatCurrency(price) : ""
    }
    card.dataset.lineSubtotal = price != null ? String(price) : "0"
    this.#updateTotal()
  }

  #fieldValue(card, field) {
    return card.querySelector(`[data-line-field="${field}"]`)?.value?.trim() || ""
  }

  #formatCurrency(amount) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)
  }
}
